import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiSearch, FiBriefcase, FiZap, FiList, FiTrendingUp, FiEdit2, FiCheck, FiX, FiUsers, FiBarChart2, FiEye, FiTrash2, FiCopy, FiExternalLink, FiFilter } from 'react-icons/fi';
import "../index.css";
import "./EmployeeDashboard.css";
import "./EmployeeLeaves.css";

const API_BASE_URL = 'https://ingrainhirebackend.ingrainsystems.com/api';

const ROLE_MAPPING = {
  "Doctor": ["Consultant", "Medical Officer", "Physician"],
  "MERN Stack Developer": ["Web Developer", "Software Engineer", "Frontend Developer", "Backend Developer"],
  "Product Specialist": ["Marketing Executive", "Sales", "Business Development"],
  "Staff Nurse": ["Nursing Staff", "Nurse"],
};

function JobPositionsStatus() {
  const [jobs, setJobs] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingJobId, setEditingJobId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState("All");
  const [selectedRole, setSelectedRole] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [roleSearchQuery, setRoleSearchQuery] = useState("");
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const roleDropdownRef = useRef(null);

  const navigate = useNavigate();

  // Get clientId from localStorage
  const getClientId = () => {
    return localStorage.getItem('clientId') || localStorage.getItem('clientCustomId') || '';
  };

  useEffect(() => {
    const clientId = getClientId();
    if (clientId) {
      fetchData();
    } else {
      setLoading(false);
    }

    const handleClickOutside = (event) => {
      if (roleDropdownRef.current && !roleDropdownRef.current.contains(event.target)) {
        setIsRoleDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchData = async () => {
    const clientId = getClientId();
    if (!clientId) {
      console.error("No clientId found");
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      // Fetch Jobs - response mein jobPosts array hai
      const jobsRes = await axios.get(`${API_BASE_URL}/jobs/all/${clientId}`);
      console.log("Jobs Response:", jobsRes.data);
      
      if (jobsRes.data.success) {
        // Response mein jobPosts array hai
        const jobsData = jobsRes.data.jobPosts || [];
        setJobs(jobsData);
        console.log("Jobs set:", jobsData.length);
      } else {
        setJobs([]);
      }
      
      // Fetch Employees
      try {
        const empsRes = await axios.get(`${API_BASE_URL}/employees/get-employees/${clientId}`);
        console.log("Employees Response:", empsRes.data);
        
        if (empsRes.data.success || Array.isArray(empsRes.data)) {
          const empData = Array.isArray(empsRes.data) ? empsRes.data : empsRes.data.employees || empsRes.data.data || [];
          setEmployees(empData);
          console.log("Employees set:", empData.length);
        } else {
          setEmployees([]);
        }
      } catch (empErr) {
        console.error("Failed to fetch employees:", empErr);
        setEmployees([]);
      }
      
    } catch (err) {
      console.error("Failed to fetch data:", err);
      setJobs([]);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const getEmployeeCount = (dept, role) => {
    if (!role) return 0;
    
    const roleAliases = [role.toLowerCase().trim(), ...(ROLE_MAPPING[role] || []).map(r => r.toLowerCase())];
    
    return employees.filter(emp => {
        const empDept = (emp.department || emp.dept || "").toLowerCase().trim();
        const empRole = (emp.role || emp.position || emp.designation || "").toLowerCase().trim();
        const empStatus = (emp.status || "").toLowerCase().trim();
        
        // Match department if specified
        const deptMatch = (dept && dept.toLowerCase() !== 'general' && dept.toLowerCase() !== 'all') 
            ? empDept.includes(dept.toLowerCase().trim()) 
            : true;

        // Match role or any of its aliases
        const roleMatch = roleAliases.some(alias => empRole.includes(alias));
        
        return deptMatch && roleMatch && empStatus === "active";
    }).length;
  };

  const startEditing = (job, field) => {
    setEditingJobId(job._id);
    setEditingField(field);
    setEditValue(job[field] === 'General' ? '' : (job[field] || ''));
  };

  const cancelEditing = () => {
    setEditingJobId(null);
    setEditingField(null);
    setEditValue("");
  };

  const saveEdit = async (jobId) => {
    const clientId = getClientId();
    if (!clientId) return;
    
    setSaving(true);
    try {
      const updateData = { 
        [editingField]: editingField === 'vacancies' ? Number(editValue) : editValue,
        clientId: clientId
      };
      const res = await axios.put(`${API_BASE_URL}/jobs/${jobId}`, updateData);
      if (res.data.success) {
        setJobs(prev => prev.map(j => j._id === jobId ? { ...j, ...updateData } : j));
        setEditingJobId(null);
        setEditingField(null);
      } else {
        alert("Update failed: " + (res.data.message || "Unknown error"));
      }
    } catch (err) {
      console.error("Failed to update:", err);
      alert("Update failed: " + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const clientId = getClientId();
  if (!clientId) {
    return (
      <div className="w-full min-h-screen p-4 lg:p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-4 border-indigo-50 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Please login to access job positions</p>
        </div>
      </div>
    );
  }

  // Get unique departments from jobs
  const getUniqueDepartments = () => {
    const depts = jobs.map(job => (job.department || 'General').trim());
    return ["All", ...new Set(depts)].sort();
  };

  // Get unique roles from jobs
  const getUniqueRoles = () => {
    const roles = jobs.map(job => job.role).filter(Boolean);
    return ["All", ...new Set(roles)].sort();
  };

  // Get unique roles for dropdown (without "All")
  const getRolesForDropdown = () => {
    const roles = jobs.map(job => job.role).filter(Boolean);
    const uniqueRoles = [...new Set(roles)].sort();
    return uniqueRoles.map((name, index) => ({ _id: index, name }));
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery("");
    setRoleFilter("");
    setDateFilter("");
    setRoleSearchQuery("");
    setSelectedDepartment("All");
    setSelectedRole("All");
  };

  // Filter jobs based on selection
  const getDisplayedJobs = () => {
    let filtered = [...jobs];
    
    // Filter by search query (role or skills)
    if (searchQuery) {
      filtered = filtered.filter(job => {
        const matchesRole = job.role?.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesSkills = job.skills?.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesRole || matchesSkills;
      });
    }
    
    // Filter by role dropdown
    if (roleFilter) {
      filtered = filtered.filter(job => job.role === roleFilter);
    }
    
    // Filter by date
    if (dateFilter) {
      filtered = filtered.filter(job => {
        const jobDate = new Date(job.createdAt || job.appliedAt).toISOString().slice(0, 10);
        return jobDate === dateFilter;
      });
    }
    
    // Filter by department (legacy filter)
    if (selectedDepartment !== "All") {
      filtered = filtered.filter(job => {
        const jobDept = (job.department || "General").trim();
        return jobDept === selectedDepartment;
      });
    }
    
    // Filter by role (legacy filter)
    if (selectedRole !== "All") {
      filtered = filtered.filter(job => job.role === selectedRole);
    }
    
    return filtered;
  };

  const activeFiltersCount = [
    roleFilter ? 1 : 0,
    dateFilter ? 1 : 0
  ].reduce((a, b) => a + b, 0);

  const displayedJobs = getDisplayedJobs();

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin mb-4" />
        <p className="text-gray-400 text-sm animate-pulse">Synchronizing records...</p>
      </div>
    );
  }

  return (
    <div style={{backgroundColor:'white'}} className="emp-dash ">
      <main className="">
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              Positions <span>Status</span>
            </h1>
            <p className="emp-dash__subtitle">
              Monitor active roles, vacancies, and fulfillment rates.
            </p>
          </div>
          <div className="emp-dash__date-pill">
            <FiBarChart2 />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

      {/* Filters Section */}
      <div className="emp-dash__card mb-6">
        <div className="emp-dash__card-header">
          <div>
            <h3 className="emp-dash__card-title flex items-center gap-2">
              <FiSearch className="text-blue-600" /> Filter Positions
            </h3>
            <p className="emp-dash__card-desc">Search by role, skills, or date</p>
          </div>
        </div>
        <div className="emp-dash__card-body bg-gray-50/50">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            {/* Search Bar & Mobile Filter Toggle */}
            <div className="flex items-center gap-2 flex-1 w-full">
              <div className="relative flex-1 min-w-0">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
                <input
                  type="text"
                  placeholder="Search by Role or Skills..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm"
                />
              </div>

              {/* Mobile Filter Toggle Button */}
              <button
                onClick={() => setIsMobileFiltersOpen(!isMobileFiltersOpen)}
                className={`md:hidden flex items-center justify-center gap-1.5 h-9 px-3 text-xs font-semibold rounded-lg border transition-all duration-200 ${
                  isMobileFiltersOpen || activeFiltersCount > 0
                    ? 'bg-blue-50 border-blue-200 text-blue-600 shadow-sm'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <FiFilter className="text-xs" />
                <span>Filters</span>
                {activeFiltersCount > 0 && (
                  <span className="flex items-center justify-center bg-blue-600 text-white rounded-full text-[9px] w-4 h-4 font-bold leading-none">
                    {activeFiltersCount}
                  </span>
                )}
              </button>
            </div>

            {/* Collapsible Filters block */}
            <div className={`
              ${isMobileFiltersOpen ? 'grid grid-cols-1 sm:grid-cols-2 gap-3 mt-1' : 'hidden'}
              md:flex md:flex-wrap md:items-center md:gap-2 md:mt-0 w-full md:w-auto
            `}>
              {/* Role Filter Button */}
              <div className="relative w-full md:w-auto" ref={roleDropdownRef}>
                <button
                  onClick={() => setIsRoleDropdownOpen(!isRoleDropdownOpen)}
                  className={`h-9 w-full md:w-auto px-3 text-xs font-medium rounded-lg transition-all duration-200 border flex items-center justify-between md:justify-start gap-1.5 shadow-sm ${
                    roleFilter
                      ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700 hover:border-blue-700'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-1.5">
                    <FiBriefcase className="text-xs" />
                    <span>Role{roleFilter ? `: ${roleFilter}` : ''}</span>
                  </span>
                  <span className="text-[9px] opacity-60">▼</span>
                </button>

                {/* Role Filter Dropdown */}
                {isRoleDropdownOpen && (
                  <div className="absolute z-50 mt-1 w-full md:w-48 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
                    <div className="p-2 border-b border-gray-100 bg-gray-50">
                      <div className="relative">
                        <FiSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 text-xs" />
                        <input
                          type="text"
                          className="w-full py-1 pl-7 pr-2 text-xs bg-white border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                          placeholder="Search roles..."
                          value={roleSearchQuery}
                          onChange={(e) => setRoleSearchQuery(e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          autoFocus
                        />
                      </div>
                    </div>
                    <div
                      onClick={() => {
                        setRoleFilter('');
                        setIsRoleDropdownOpen(false);
                        setRoleSearchQuery('');
                      }}
                      className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer border-b border-gray-100 font-medium ${
                        !roleFilter ? 'bg-blue-50 text-blue-700' : 'text-gray-700'
                      }`}
                    >
                      All Roles
                    </div>
                    {getRolesForDropdown()
                      .filter(r => r.name.toLowerCase().includes(roleSearchQuery.toLowerCase()))
                      .map((r) => (
                        <div
                          key={r._id}
                          onClick={() => {
                            setRoleFilter(r.name);
                            setIsRoleDropdownOpen(false);
                            setRoleSearchQuery('');
                          }}
                          className={`px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer ${
                            roleFilter === r.name ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                          }`}
                        >
                          {r.name}
                        </div>
                      ))}
                    {getRolesForDropdown().filter(r => r.name.toLowerCase().includes(roleSearchQuery.toLowerCase())).length === 0 && (
                      <div className="px-3 py-2 text-xs text-gray-400 text-center">
                        No roles found
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Date Filter */}
              <div className="relative w-full md:w-[150px]">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[10px] text-gray-500 pointer-events-none">
                  Date:
                </span>
                <input
                  type="date"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                  onClick={(e) => e.target.showPicker && e.target.showPicker()}
                  className="w-full pl-12 pr-3 py-1.5 h-9 text-xs border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm"
                />
              </div>

              {/* Clear Filters Button */}
              {(searchQuery || roleFilter || dateFilter) && (
                <button
                  onClick={clearFilters}
                  className="h-9 w-full md:w-auto px-4 text-xs font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg transition-all shadow-sm"
                >
                  Clear
                </button>
              )}
            </div>

            {/* Post New Job Button (Always visible on desktop and mobile) */}
            <div className="w-full md:w-auto flex justify-end md:ml-auto">
              <button
                onClick={() => navigate('/post-new-job')}
                className="h-9 w-full md:w-auto px-4 text-xs font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition flex items-center justify-center gap-1.5 shadow-sm"
              >
                <FiPlus className="text-sm" />
                <span>Post New Job</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-20 flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
            <FiBriefcase className="text-gray-300" size={32} />
          </div>
          <h3 className="text-lg font-bold text-gray-800">No Job Positions Yet</h3>
          <p className="text-gray-500 text-sm max-w-xs mt-2">Start by posting your first job opening to see statistics here.</p>
          <button
            onClick={() => navigate('/post-new-job')}
            className="mt-6 text-indigo-600 font-bold text-sm hover:underline"
          >
            Create first post
          </button>
        </div>
      ) : displayedJobs.length === 0 ? (
        <div className="bg-white rounded-3xl border border-dashed border-gray-200 p-20 flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-4">
            <FiSearch className="text-gray-300" size={32} />
          </div>
          <h3 className="text-lg font-bold text-gray-800">No matching positions</h3>
          <p className="text-gray-500 text-sm max-w-xs mt-2">Try changing your filters to see more results.</p>
        </div>
      ) : (
        <>
          <div className="emp-dash__card mb-6">
            <div className="emp-dash__table-wrap overflow-x-auto">
              <table className="emp-dash__table min-w-[800px]">
                <thead>
                  <tr>
                    <th className="py-2 text-center font-bold text-xs uppercase tracking-wider">Role</th>
                    <th className="py-2 text-center font-bold text-xs uppercase tracking-wider">Hired</th>
                    <th className="py-2 text-center font-bold text-xs uppercase tracking-wider">Salary</th>
                    <th className="py-2 text-center font-bold text-xs uppercase tracking-wider">Location</th>
                    <th className="py-2 text-center font-bold text-xs uppercase tracking-wider">Vacancies</th>
                    <th className="py-2 text-center font-bold text-xs uppercase tracking-wider">Status</th>
                    <th className="py-2 text-center font-bold text-xs uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {displayedJobs.map((job) => {
                    const empCount = getEmployeeCount(job.department, job.role);
                    const isEditingDept = editingJobId === job._id && editingField === 'department';
                    const isEditingTotal = editingJobId === job._id && editingField === 'vacancies';

                    return (
                      <tr key={job._id} className="border-b hover:bg-gray-50 transition-colors">
                        <td className="px-2 py-2 text-center">
                          <div className="flex flex-col items-center">
                            <div className="text-sm font-bold text-gray-900">{job.role}</div>
                            <div className="flex items-center gap-1 mt-1">
                              {/* <span className="text-[9px] text-gray-400">{job.location || 'N/A'}</span> */}
                              <span className="text-gray-300">•</span>
                              {isEditingDept ? (
                                <div className="flex items-center gap-1">
                                  <input
                                    type="text"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    className="w-20 px-1 py-0.5 text-[9px] border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-100 outline-none"
                                    autoFocus
                                    placeholder="Dept..."
                                  />
                                  <button onClick={() => saveEdit(job._id)} disabled={saving} className="text-emerald-600"><FiCheck size={10} /></button>
                                  <button onClick={cancelEditing} className="text-rose-600"><FiX size={10} /></button>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 group/edit-dept">
                                  <span className={`text-[9px] font-semibold transition-colors ${!job.department || job.department === 'General' ? 'text-gray-400 italic' : 'text-indigo-600'}`}>
                                    {job.department || 'General'}
                                  </span>
                                  <button 
                                    onClick={() => startEditing(job, 'department')}
                                    className="text-gray-300 hover:text-indigo-600 opacity-0 group-hover/edit-dept:opacity-100 transition-all"
                                  >
                                    <FiEdit2 size={8} />
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <FiUsers className="text-gray-400" size={12} />
                            <span className="text-sm font-bold text-gray-600">{empCount}</span>
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <span className="text-sm font-medium text-emerald-600">{job.salary || '-'}</span>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <span className="text-sm font-medium text-gray-600">{job.location || '-'}</span>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <div className="flex items-center justify-center gap-2">
                            <FiBriefcase className="text-gray-300" size={12} />
                            {isEditingTotal ? (
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    // Allow only positive integers (no decimals, no special chars)
                                    if (value === '' || /^\d+$/.test(value)) {
                                      setEditValue(value);
                                    }
                                  }}
                                  onKeyPress={(e) => {
                                    // Prevent non-numeric characters
                                    if (!/^\d$/.test(e.key)) {
                                      e.preventDefault();
                                    }
                                  }}
                                  className="w-12 px-1 py-0.5 text-sm border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-100 outline-none"
                                  placeholder="0"
                                  autoFocus
                                />
                                <button onClick={() => saveEdit(job._id)} disabled={saving} className="text-emerald-600"><FiCheck size={10} /></button>
                                <button onClick={cancelEditing} className="text-rose-600"><FiX size={10} /></button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 group/edit-total">
                                <span className="text-sm font-medium text-gray-600">{job.vacancies ?? 0}</span>
                                <button 
                                  onClick={() => startEditing(job, 'vacancies')}
                                  className="text-gray-300 hover:text-indigo-600 opacity-0 group-hover/edit-total:opacity-100 transition-all"
                                >
                                  <FiEdit2 size={10} />
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
                            job.status === 'active' 
                              ? 'bg-emerald-50 text-emerald-600' 
                              : job.status === 'draft'
                              ? 'bg-amber-50 text-amber-600'
                              : 'bg-gray-100 text-gray-500'
                          }`}>
                            {job.status || 'active'}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-center">
                          <div className="flex justify-center gap-2">
                            <button 
                              onClick={() => navigate(`/post-new-job?editId=${job._id}`)}
                              className="text-blue-500 hover:text-blue-700"
                              title="View/Edit Job"
                            >
                              <FiEye size={14} />
                            </button>
                            <button 
                              onClick={() => navigate(`/post-new-job?editId=${job._id}`)}
                              className="text-yellow-500 hover:text-yellow-700"
                              title="Edit Job"
                            >
                              <FiEdit2 size={14} />
                            </button>
                            <button 
                              onClick={() => navigate(`/post-new-job?role=${encodeURIComponent(job.role)}&department=${encodeURIComponent(job.department || 'General')}`)}
                              className="text-gray-400 hover:text-indigo-600"
                              title="Post Similar Job"
                            >
                              <FiPlus size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile View — visible only on screens < 640px */}
            <div className="emp-dash__mobile-list">
              {displayedJobs.map((job) => {
                const empCount = getEmployeeCount(job.department, job.role);
                const isEditingDept = editingJobId === job._id && editingField === 'department';
                const isEditingTotal = editingJobId === job._id && editingField === 'vacancies';

                return (
                  <div key={job._id} className="emp-dash__mobile-item">
                    {/* Top row: Role + Status */}
                    <div className="emp-dash__mobile-item-top">
                      <span className="emp-dash__mobile-date">{job.role}</span>
                      <span className={`px-2 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
                        job.status === 'active' 
                          ? 'bg-emerald-50 text-emerald-600' 
                          : job.status === 'draft'
                          ? 'bg-amber-50 text-amber-600'
                          : 'bg-gray-100 text-gray-500'
                      }`}>
                        {job.status || 'active'}
                      </span>
                    </div>

                    {/* Info grid */}
                    <div className="emp-dash__mobile-grid">
                      <div className="emp-dash__mobile-field">
                        <span>Hired</span>
                        <div className="flex items-center gap-1 mt-0.5">
                          <FiUsers className="text-gray-400" size={10} />
                          <span className="text-xs font-bold text-gray-600">{empCount}</span>
                        </div>
                      </div>

                      <div className="emp-dash__mobile-field">
                        <span>Salary</span>
                        <span className="text-xs font-medium text-emerald-600 mt-0.5 block">{job.salary || '-'}</span>
                      </div>

                      <div className="emp-dash__mobile-field">
                        <span>Department</span>
                        {isEditingDept ? (
                          <div className="flex items-center gap-1 mt-0.5">
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              className="w-20 px-1 py-0.5 text-[9px] border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-100 outline-none"
                              autoFocus
                              placeholder="Dept..."
                            />
                            <button onClick={() => saveEdit(job._id)} disabled={saving} className="text-emerald-600"><FiCheck size={10} /></button>
                            <button onClick={cancelEditing} className="text-rose-600"><FiX size={10} /></button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1 group/edit-dept mt-0.5">
                            <span className={`text-xs font-semibold transition-colors ${!job.department || job.department === 'General' ? 'text-gray-400 italic' : 'text-indigo-600'}`}>
                              {job.department || 'General'}
                            </span>
                            <button 
                              onClick={() => startEditing(job, 'department')}
                              className="text-gray-300 hover:text-indigo-600 transition-all animate-in fade-in"
                            >
                              <FiEdit2 size={8} />
                            </button>
                          </div>
                        )}
                      </div>

                      <div className="emp-dash__mobile-field">
                        <span>Vacancies</span>
                        <div className="flex items-center gap-1 mt-0.5">
                          <FiBriefcase className="text-gray-300" size={10} />
                          {isEditingTotal ? (
                            <div className="flex items-center gap-1">
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '' || /^\d+$/.test(value)) {
                                    setEditValue(value);
                                  }
                                }}
                                onKeyPress={(e) => {
                                  if (!/^\d$/.test(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                className="w-10 px-1 py-0.5 text-xs border border-indigo-300 rounded focus:ring-2 focus:ring-indigo-100 outline-none"
                                placeholder="0"
                                autoFocus
                              />
                              <button onClick={() => saveEdit(job._id)} disabled={saving} className="text-emerald-600"><FiCheck size={10} /></button>
                              <button onClick={cancelEditing} className="text-rose-600"><FiX size={10} /></button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1 group/edit-total">
                              <span className="text-xs font-medium text-gray-600">{job.vacancies ?? 0}</span>
                              <button 
                                onClick={() => startEditing(job, 'vacancies')}
                                className="text-gray-300 hover:text-indigo-600 transition-all"
                              >
                                <FiEdit2 size={10} />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="emp-dash__mobile-field">
                        <span>Location</span>
                        <span className="text-xs text-gray-600 mt-0.5 block">{job.location || 'N/A'}</span>
                      </div>
                    </div>



                    {/* Action buttons */}
                    <div className="flex items-center gap-3 mt-3 pt-3 border-t border-gray-100">
                      <button 
                        onClick={() => navigate(`/post-new-job?editId=${job._id}`)}
                        className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                        title="View/Edit Job"
                      >
                        <FiEye size={12} /> View
                      </button>
                      <button 
                        onClick={() => navigate(`/post-new-job?editId=${job._id}`)}
                        className="flex items-center gap-1 text-xs text-yellow-600 hover:text-yellow-800 font-medium"
                        title="Edit Job"
                      >
                        <FiEdit2 size={12} /> Edit
                      </button>
                      <button 
                        onClick={() => navigate(`/post-new-job?role=${encodeURIComponent(job.role)}&department=${encodeURIComponent(job.department || 'General')}`)}
                        className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-800 font-medium ml-auto"
                        title="Post Similar Job"
                      >
                        <FiPlus size={12} /> Similar Job
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          
          {/* Stats Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="emp-dash__card p-6">
                  <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                          <FiUsers size={20} />
                      </div>
                      <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Active Workforce</p>
                          <h4 className="text-xl font-bold text-gray-900">
                              {employees.length}
                          </h4>
                      </div>
                  </div>
              </div>
              <div className="emp-dash__card p-6">
                  <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600">
                          <FiZap size={20} />
                      </div>
                      <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Total Planned Positions</p>
                          <h4 className="text-xl font-bold text-gray-900">
                              {displayedJobs.reduce((acc, job) => acc + (job.vacancies ?? 1), 0)}
                          </h4>
                      </div>
                  </div>
              </div>

              <div className="emp-dash__card p-6">
                  <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
                          <FiTrendingUp size={20} />
                      </div>
                      <div>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Fulfillment Rate</p>
                          <h4 className="text-xl font-bold text-gray-900">
                              {(() => {
                                  const totalPositions = displayedJobs.reduce((acc, job) => acc + (job.vacancies ?? 1), 0);
                                  if (totalPositions === 0) return "0%";
                                  const totalActiveMatched = displayedJobs.reduce((acc, job) => acc + getEmployeeCount(job.department, job.role), 0);
                                  return `${Math.round((totalActiveMatched / totalPositions) * 100)}%`;
                              })()}
                          </h4>
                      </div>
                  </div>
              </div>
          </div>
        </>
      )}
      </main>
    </div>
  );
}

export default JobPositionsStatus;